import { Component, EventEmitter, HostListener, Inject, OnInit, Output, ViewEncapsulation } from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { Router } from "@angular/router";

import { MatDialogRef, MAT_DIALOG_DATA } from "@angular/material/dialog";

import { Observable } from "rxjs";
import { select, Store } from "@ngrx/store";
import { AppState, sitesStateIsLoading } from "src/app/store/reducers";
import { createMultimedia, updateMultimedia } from "src/app/store/actions/sites/sites.actions";

import { TranslateService } from "@ngx-translate/core";
import { SnackbarService } from "src/app/shared/services/snackbar/snackbar.service";

import { Dialog, GuardConfirmationDialogData } from "src/app/models/dialog/dialog.model";
import { SiteMultimediaDTO } from "src/app/models/Sites/siteMultimediaDTO";
import { CommunicationMethodTypeCode, PROTOCOL_FORM_PARAMS } from "src/app/models/protocol/protocol.model";
import { SnackbarType } from "src/app/models/snackbar/snackbar.model";
import { isAuthenticated } from "src/app/shared/utils/authenticationUtils";
import { setCommunicationMethodData, setProtocolFormValues } from "src/app/shared/utils/protocolFormUtils";

import { CanComponentDeactivate } from "src/app/shared";
import { ConfirmationDialogComponent } from "src/app/shared/modules/dialogs/confirmation-dialog/confirmation-dialog.component";

//Multimedia Detail Form
const MULTIMEDIA_FORM_PARAMS = {
  enabled: "enabled",
  multimediaNumber: "multimediaNumber",
  serialNumber: "serialNumber",
  description: "description",
  additionalData: "additionalData",
  protocol: "protocol"
};

@Component({
  selector: "app-multimedia-form-dialog",
  templateUrl: "./multimedia-form-dialog.component.html",
  styleUrls: ["./multimedia-form-dialog.component.scss"],
  encapsulation: ViewEncapsulation.None,
})
export class MultimediaFormDialogComponent implements OnInit, CanComponentDeactivate {
  @Output() getMultimediaForm: EventEmitter<FormGroup> = new EventEmitter<FormGroup>();

  isLoading$: Observable<boolean>;
  
  siteCode: string;
  currentMultimedia: SiteMultimediaDTO;

  multimediaDataForm: FormGroup;

  isCreate: boolean = false;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    private dialogRef: MatDialogRef<MultimediaFormDialogComponent>,
    private fb: FormBuilder,
    private router: Router,
    private store: Store<AppState>,
    private snackbarService: SnackbarService,
    private translate: TranslateService
  ) {
    this.isLoading$ = this.store.pipe(select(sitesStateIsLoading));
  }
  
  ngOnInit(): void {
    this.setDialogData();
    this.createMultimediaForm();
    this.setFormValues(this.currentMultimedia);
  }

  setDialogData() {
    this.isCreate = this.data.isCreate;
    this.siteCode = this.data.siteCode;
    this.currentMultimedia = this.data.currentMultimedia;
  }

  createMultimediaForm() {
    this.multimediaDataForm = this.fb.group({
      [MULTIMEDIA_FORM_PARAMS.enabled]: [true],
      [MULTIMEDIA_FORM_PARAMS.multimediaNumber]: [
        null,
        [Validators.required, Validators.min(1), Validators.max(255)],
      ],
      [MULTIMEDIA_FORM_PARAMS.serialNumber]: [null, Validators.maxLength(50)],
      [MULTIMEDIA_FORM_PARAMS.description]: [null, Validators.maxLength(45)],
      [MULTIMEDIA_FORM_PARAMS.additionalData]: [null],
      [MULTIMEDIA_FORM_PARAMS.protocol]: this.fb.group({}),
    });
    this.onAdditionalDataChanges(this.multimediaDataForm);
    this.subscribeFormChanges(this.multimediaDataForm);
  }

  subscribeFormChanges(form: FormGroup) {
    form.valueChanges.subscribe(() => {
      this.getMultimediaFormPristine();
    });
  }

  onAdditionalDataChanges(form: FormGroup) {
    form.get(MULTIMEDIA_FORM_PARAMS.additionalData).valueChanges.subscribe((additionalData) => {
      if (additionalData) {
        try {
          const json = JSON.parse(additionalData);
          if (json && typeof json === "object") {
            return json;
          } else {
            form.get(MULTIMEDIA_FORM_PARAMS.additionalData).setErrors({ parseError: true });
          }
        } catch (e) {
          form.get(MULTIMEDIA_FORM_PARAMS.additionalData).setErrors({ parseError: true });
        }
      }
    })
  }

  setFormValues(multimediaDevice: SiteMultimediaDTO, duplication?: boolean) {
    this.multimediaDataForm.controls.enabled.setValue(
      multimediaDevice?.enabled ?? true
    );
    this.multimediaDataForm.controls.multimediaNumber.setValue(
      duplication ? null : multimediaDevice?.multimediaNumber ?? null
    );
    this.multimediaDataForm.controls.serialNumber.setValue(
      duplication ? null : multimediaDevice?.serialNumber ?? null
    );
    this.multimediaDataForm.controls.description.setValue(
      multimediaDevice?.description ?? null
    );
    this.multimediaDataForm.controls.additionalData.setValue(
      multimediaDevice?.additionalData ? JSON.stringify(multimediaDevice.additionalData, null, "\t") : null
    );

    setProtocolFormValues(this.multimediaDataForm, multimediaDevice);
  }

  duplicate() {
    this.setFormValues(this.currentMultimedia, true);

    this.isCreate = true;
    this.currentMultimedia = null;
  }

  onSubmit() {
    if (!this.multimediaDataForm.valid) {
      this.multimediaDataForm.markAllAsTouched();
      this.snackbarService.show({
        message: this.translate.instant("SiteManagement.formInvalid"),
        type: SnackbarType.ERROR,
      });
      return;
    }
    
    if (this.multimediaDataForm.pristine && !this.isCreate) {
      this.snackbarService.show({
        message: this.translate.instant("SiteManagement.NoChanges"),
        type: SnackbarType.WARNING,
      });
      return;
    }

    const protocolFormGroup = this.multimediaDataForm.get(
      MULTIMEDIA_FORM_PARAMS.protocol
    ) as FormGroup;
    let multimediaDevice: SiteMultimediaDTO = {
      siteCode: this.siteCode ? this.siteCode : null,
      code: this.currentMultimedia?.code,
      enabled: this.multimediaDataForm.controls.enabled.value ?? false,
      multimediaNumber: this.multimediaDataForm.controls.multimediaNumber.value ?? null,
      serialNumber: this.multimediaDataForm.controls.serialNumber.value ?? null,
      description: this.multimediaDataForm.controls.description.value ?? null,
      additionalData: this.multimediaDataForm.controls.additionalData.value
        ? JSON.parse(this.multimediaDataForm.controls.additionalData.value)
        : null,
      communicationMethodTypeCode: protocolFormGroup.get(
        PROTOCOL_FORM_PARAMS.communicationMethodTypeCode
      ).value
        ? CommunicationMethodTypeCode.SERIAL_CONNECTION
        : CommunicationMethodTypeCode.IP_PROTOCOL,
      communicationMethodData: setCommunicationMethodData(protocolFormGroup),
    };

    if (this.isCreate) {
      this.store.dispatch(createMultimedia({ multimediaDevice }));
    } else {
      this.store.dispatch(updateMultimedia({ multimediaDevice }));
    }

    this.multimediaDataForm.markAsUntouched();
    this.multimediaDataForm.markAsPristine();
    this.getMultimediaFormPristine();
  }

  getMultimediaFormPristine() {
    this.getMultimediaForm.emit(this.multimediaDataForm);
  }

  close() {
    this.dialogRef.close();
  }

  @HostListener("window:beforeunload")
  canDeactivate(): Observable<boolean> | boolean {
    return this.isNavigationAllowed();
  }

  isNavigationAllowed(): Observable<boolean> | boolean {
    if (!isAuthenticated(this.router.url) || this.multimediaDataForm.pristine) {
      return true;
    } else {
      return false;
    }
  }
  
  confirmDialog(dialog: any): Observable<boolean> {
    const dialogData: Dialog = GuardConfirmationDialogData;
    const dialogRef = dialog.open(ConfirmationDialogComponent, dialogData);
    return dialogRef.afterClosed();
  }
}
